import {
  Button,
  DatePicker,
  Flex,
  Heading,
  Spinner,
  Text,
  TextInput,
  createForm,
  useToasts,
} from '@applyboard/crystal-ui';
import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { ErrorHandler } from '~/pages/ErrorPage';
import { useIntakeDetailsContext } from '../context/IntakeDetailsContext';
import { capitalize } from '~/utils/capitalize';
import { usePatchIntake } from '../data/usePatchIntake';
import { IntakeFormActions, ProgramIntakeFormFields } from './IntakeFormActions';
import { formatCurrency } from '~/utils';
import { getChangedIntakeFields } from '../utils/getChangedIntakeFields';

const minDate = new Date(1900, 0, 1).toISOString();
const maxDate = new Date(2100, 0, 1).toISOString();

function addTimezoneOffset(isoString: string | undefined | null): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  date.setHours(date.getHours() + 6);
  return date.toISOString();
}

const intakeForm = createForm<ProgramIntakeFormFields>();

type DateFieldWithDynamicMinProps = {
  allowedToEdit: boolean;
  dependsOnField: keyof ProgramIntakeFormFields;
  name: keyof ProgramIntakeFormFields;
  label: string;
  required?: boolean;
};

function DateFieldWithDynamicMin({
  allowedToEdit,
  dependsOnField,
  name,
  label,
  required = false,
}: DateFieldWithDynamicMinProps) {
  const { Field, useFieldValues, useSetFieldValues } = intakeForm;
  const fieldValues = useFieldValues([dependsOnField, name]);
  const dependsOnValue = fieldValues[dependsOnField];
  const currentValue = fieldValues[name];
  const setFieldValues = useSetFieldValues();
  const dynamicMinDate = dependsOnValue || minDate;

  useEffect(() => {
    if (allowedToEdit && dependsOnValue && currentValue) {
      const startDate = new Date(dependsOnValue);
      const endDate = new Date(currentValue);
      if (endDate < startDate) {
        setFieldValues({ [name]: '' } as Partial<ProgramIntakeFormFields>);
      }
    }
  }, [dependsOnValue, currentValue, name, setFieldValues]);

  return (
    <Field
      as={DatePicker}
      name={name}
      label={
        <Text variant="bodyS" contrast="mid">
          {label}
        </Text>
      }
      granularity="day"
      minDate={dynamicMinDate}
      maxDate={maxDate}
      disabled={!allowedToEdit}
      required={required ? `${label} is required` : undefined}
    />
  );
}

export function ProgramIntakeInformation() {
  const toast = useToasts();
  const { intake, isLoadingIntake, isIntakeError, intakeError } = useIntakeDetailsContext();
  const [allowedToEdit, setAllowedToEdit] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const { Form, Field, useFieldValues } = intakeForm;
  const { patchIntake, isPatchingIntake } = usePatchIntake(intake?.id || '');

  const handleResetForm = () => {
    setFormKey((prev) => prev + 1);
    setAllowedToEdit(false);
  };

  if (!isIntakeError && isLoadingIntake) {
    return (
      <Flex p={5} gap={5} align="center" direction="column" role="progressbar">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (isIntakeError || !intake) {
    return (
      <ErrorHandler
        error={intakeError || new Error('Program intake not found')}
        customMessage="Failed to fetch the program intake details."
      />
    );
  }

  const formDefaultValues: ProgramIntakeFormFields = {
    programName: intake.programName || '',
    campusName: intake.campusName || '',
    intakeTerm: intake.intakeTermName || '',
    firstDayOfClass: addTimezoneOffset(intake.firstDayOfClass),
    lastDayOfClass: addTimezoneOffset(intake.lastDayOfClass),
    applicationFee: intake.applicationFee ? formatCurrency(Number(intake.applicationFee)) : 'N/A',
    acceptingApplicationsStartsAt: intake.acceptingApplicationsStartsAt || '',
    acceptingApplicationsStopsAt: intake.acceptingApplicationsStopsAt || '',
    deferralDeadlineAt: intake.deferralDeadlineAt || '',
    refundDeadlineAt: intake.refundDeadlineAt || '',
    tuition: intake.tuition ? formatCurrency(Number(intake.tuition)) : 'N/A',
    tuitionPeriod: capitalize(intake.tuitionPeriod) || '',
  };

  return (
    <Flex direction="column" gap={8}>
      <FormWrapper>
        <Form
          key={formKey}
          defaultValues={formDefaultValues}
          onSubmit={async (values) => {
            const changedFields = getChangedIntakeFields(values, formDefaultValues);

            if (Object.keys(changedFields).length === 0) {
              return;
            }

            try {
              await patchIntake(changedFields);

              toast.positive('Your changes were successfully saved.');
              setAllowedToEdit(false);
            } catch (error) {
              toast.negative(new Error('Failed to save changes. Please try again.', { cause: error }));
            }
          }}
        >
          <Flex direction="column" gap={4}>
            <Flex direction="row" align="center" gap={8} justify="between">
              <Heading contrast="high" level={2} variant="titleS">
                Program Intake
              </Heading>
              {!allowedToEdit ? (
                <Button intent="primary" emphasis="highlighted" onClick={() => setAllowedToEdit(true)}>
                  Edit Intake
                </Button>
              ) : null}
            </Flex>
            <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
              <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
                <Field
                  as={TextInput}
                  name="programName"
                  label={
                    <Text variant="bodyS" contrast="mid">
                      Program Name
                    </Text>
                  }
                  disabled
                />
              </Flex.Item>
              <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
                <Field
                  as={TextInput}
                  name="campusName"
                  label={
                    <Text variant="bodyS" contrast="mid">
                      Campus Name
                    </Text>
                  }
                  disabled
                />
              </Flex.Item>
              <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
                <Field
                  as={TextInput}
                  name="intakeTerm"
                  label={
                    <Text variant="bodyS" contrast="mid">
                      Intake Term
                    </Text>
                  }
                  disabled
                />
              </Flex.Item>
            </Flex>

            <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
              <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
                <Field
                  as={DatePicker}
                  name="firstDayOfClass"
                  label={
                    <Text variant="bodyS" contrast="mid">
                      First Day of Class
                    </Text>
                  }
                  granularity="day"
                  minDate={minDate}
                  maxDate={maxDate}
                  disabled={!allowedToEdit}
                  required="First Day of Class is required"
                />
              </Flex.Item>
              <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
                <DateFieldWithDynamicMin
                  allowedToEdit={allowedToEdit}
                  dependsOnField="firstDayOfClass"
                  name="lastDayOfClass"
                  label="Last Day of Class"
                  required
                />
              </Flex.Item>
              <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
                <Field
                  as={TextInput}
                  name="applicationFee"
                  label={
                    <Text variant="bodyS" contrast="mid">
                      Application Fee
                    </Text>
                  }
                  disabled={!allowedToEdit}
                  required="Application Fee is required"
                />
              </Flex.Item>
            </Flex>

            <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
              <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
                <Field
                  as={TextInput}
                  name="tuition"
                  label={
                    <Text variant="bodyS" contrast="mid">
                      Tuition
                    </Text>
                  }
                  disabled
                />
              </Flex.Item>
              <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
                <Field
                  as={TextInput}
                  name="tuitionPeriod"
                  label={
                    <Text variant="bodyS" contrast="mid">
                      Tuition Period
                    </Text>
                  }
                  disabled
                />
              </Flex.Item>
            </Flex>

            <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
              <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
                <Field
                  as={DatePicker}
                  name="acceptingApplicationsStartsAt"
                  label={
                    <Text variant="bodyS" contrast="mid">
                      Accepting Applications Starts At
                    </Text>
                  }
                  granularity="day"
                  minDate={minDate}
                  maxDate={maxDate}
                  disabled={!allowedToEdit}
                />
              </Flex.Item>
              <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
                <DateFieldWithDynamicMin
                  allowedToEdit={allowedToEdit}
                  dependsOnField="acceptingApplicationsStartsAt"
                  name="acceptingApplicationsStopsAt"
                  label="Accepting Applications Closes At"
                />
              </Flex.Item>
            </Flex>

            <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
              <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
                <Field
                  as={DatePicker}
                  name="deferralDeadlineAt"
                  label={
                    <Text variant="bodyS" contrast="mid">
                      Deferral Deadline At
                    </Text>
                  }
                  granularity="day"
                  minDate={minDate}
                  maxDate={maxDate}
                  disabled={!allowedToEdit}
                />
              </Flex.Item>
              <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
                <DateFieldWithDynamicMin
                  allowedToEdit={allowedToEdit}
                  dependsOnField="deferralDeadlineAt"
                  name="refundDeadlineAt"
                  label="Refund Deadline At"
                />
              </Flex.Item>
            </Flex>
          </Flex>
          <IntakeFormActions
            allowedToEdit={allowedToEdit}
            isPatchingIntake={isPatchingIntake}
            useFieldValues={useFieldValues}
            formDefaultValues={formDefaultValues}
            setAllowedToEdit={setAllowedToEdit}
            onReset={handleResetForm}
          />
        </Form>
      </FormWrapper>
    </Flex>
  );
}

const FormWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
`;
